import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import viewpaths from "global/constants/ViewPathConstants";
import {
  isNetworkError,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import { RegisterUser, User } from "models/interfaces";
import history from "utils/history";
import {
  makeCall,
  getNoAuthCallParams,
  getAccessToken,
  getMFACallParams,
  getCallParamsWithoutCheckId,
  getCallParams,
  makeCallText,
  getNoAuthSearchedCallParams,
} from "utils/service";
import { store } from "utils/store";

export async function login(email: string, password: string, token: string) {
  const body = {
    email: email,
    pwd: password,
    captchaToken: token,
    captchaVersion: "v3",
  };
  try {
    const callParams = getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.LOGIN, callParams);
    return response;
  } catch (error: any) {
    if (error.message === strings.pendingVerificationErrorMessage) {
      history.push({
        pathname: viewpaths.verificationPendingViewPath,
        state: { email: email, isFromLoginScreen: true },
      });
    } else if (!isNetworkError(error)) {
      // A transport failure's own message is browser jargon ("Failed to
      // fetch"). `doLogin`'s catch runs `handleErrorMessage`, which words it
      // for the user — so stay quiet here rather than showing both.
      openErrorNotification(error.message);
    }
    throw error;
  }
}

export async function forgotPassword(email: string, token: string) {
  const body = {
    email: email,
    captchaToken: token,
    captchaVersion: "v3",
  };
  try {
    const callParams = await getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.RESET_PASSWORD, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function verifyTempPassword(token: string) {
  try {
    const callParams = await getNoAuthCallParams("POST", token);
    const response: any = await makeCall(urls.VERIFY_TEMP_PASSWORD, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function updatePwd(user: User, token: string) {
  try {
    const body = {
      ...user,
      captchaVersion: "v3",
    };
    const callParams = await getAccessToken("POST", body, token);
    const response: any = await makeCall(urls.UPDATE_PASSWORD, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function verifyTOTP(email: string, authenticatorCode: number) {
  try {
    const body = {
      email,
      authenticatorCode,
    };
    const callParams = getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.VERIFY_TOTP, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export const activateMFA = async (
  token: string,
  email: string,
  password: string,
  authenticatorCode: string
) => {
  try {
    const body = {
      email,
      pwd: password,
      authenticatorCode,
    };
    const callParams = await getMFACallParams(token, "POST", body);
    const response: any = await makeCall(urls.ACTIVATE_MFA, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const generateQRCodeForMFA = async (token: string) => {
  try {
    const callParams = await getMFACallParams(token, "GET");
    const response: any = await makeCall(urls.MFA_GET_QR_CODE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchPreAuthSecretKey = async (token: string) => {
  try {
    const callParams = await getMFACallParams(token, "GET");
    const response: any = await makeCallText(
      urls.MFA_GET_PREAUTH_KEY,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const saveFirebaseToken = async (
  accessToken: string,
  token: string,
  email: string
) => {
  try {
    const body = {
      userId: email,
      token,
    };
    const callParams = await getMFACallParams(accessToken, "POST", body);
    const response: any = await makeCall(urls.SAVE_FIREBASE_TOKEN, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const removeDeviceId = async () => {
  try {
    const deviceId = store.getState().auth.deviceId;
    const user = {
      deviceIds: isTruthy(deviceId) ? [deviceId] : [],
    };
    const callParams = getCallParamsWithoutCheckId("PATCH", user);
    const response: any = await makeCall(urls.REMOVE_DEVICE_ID, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const createNewAccount = async (user: RegisterUser) => {
  try {
    const body = {
      ...user,
      captchaVersion: "v3",
    };
    const callParams = getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.REGISTER_USER, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export async function resendAccountVerificationNotification(userEmail: string) {
  try {
    const callParams = await getNoAuthCallParams("POST", { email: userEmail });
    const response: any = await makeCall(
      urls.RESEND_ACCOUNT_VERIFICATION_EMAIL,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function oAuthLogin(token: string, provider: string) {
  try {
    const url = `${urls.OAUTH_LOGIN}/${provider}`;
    const callParams = getNoAuthSearchedCallParams("POST", token);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export function initiateSamlLogin(email: string, app: string = strings.inkfree) {
  const url = `${urls.SAML_LOGIN_INIT}?email=${encodeURIComponent(
    email,
  )}&app=${encodeURIComponent(app)}`;
  window.location.assign(url);
}

export interface SamlCheckResponse {
  ssoConfigured: boolean;
}

export async function checkSamlRegistration(
  email: string,
  app: string = strings.inkfree,
): Promise<SamlCheckResponse> {
  const url = `${urls.SAML_LOGIN_CHECK}?email=${encodeURIComponent(
    email,
  )}&app=${encodeURIComponent(app)}`;
  const callParams = getNoAuthCallParams("GET");
  const response: SamlCheckResponse = await makeCall(url, callParams);
  return response;
}

/**
 * Trades the single-use code from the SSO callback for the logged-in user -- the same shape
 * POST /user/login returns. The code is worthless on its own, dies on first use, and expires
 * in 60s -- which is why it is safe to carry in the callback URL when the user's tokens are not.
 */
export async function exchangeSsoCode(code: string): Promise<any> {
  const callParams = getNoAuthCallParams("POST", { code });
  const user = await makeCall(urls.SAML_TOKEN_EXCHANGE, callParams);
  if (!user?.authToken) {
    throw new Error("The sign-in link is no longer valid. Please sign in again.");
  }
  return user;
}

export interface SamlLogoutResponse {
  sloRequired: boolean;
  method?: "REDIRECT" | "POST";
  url?: string;
  parameters?: Record<string, string>;
}

export async function samlLogout(
  loginHint?: string,
  token?: string,
): Promise<SamlLogoutResponse> {
  try {
    const url = loginHint
      ? `${urls.SAML_LOGOUT}?loginHint=${encodeURIComponent(loginHint)}`
      : urls.SAML_LOGOUT;
    // Core keeps the pending SAML LogoutRequest in its session, so the browser has to store the
    // JSESSIONID this call sets; without it the IdP's LogoutResponse ends in slo_failed.
    const callParams = {
      ...(token
        ? await getMFACallParams(token, "POST")
        : await getCallParams("POST")),
      credentials: "include",
    };
    const response: SamlLogoutResponse = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function oAuthSignUp(provider: string, user: any) {
  try {
    const url = `${urls.OAUTH_SIGN_UP}/${provider}`;
    const callParams = getNoAuthCallParams("POST", user);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}
